import { ArrayUtil, LocalUserSettings, Navigation, ObjectUtil, StringUtil } from "@mcleod/core";
import { ImageNames } from "@mcleod/images";
import { Button } from "../../components/button/Button";
import { ButtonProps } from "../../components/button/ButtonProps";
import { Component } from "../..";
import { ContextMenuLabelProps } from "../../components/contextmenu/ContextMenuLabelProps";
import { OpenType } from "./OpenType";

export interface OpenButtonsProps {
    allowedTypes: OpenType[];
    defaultType: OpenType;
    doBeforeOpen?: (type: OpenType) => Promise<void>;
    doAfterOpen?: (type: OpenType) => Promise<void>;
    itemDescr: string;
    layoutPath: string;
    localSettingsKey: string;
    openInSameTabHandler?:  (url: string) => void;
    parentComponent: Component;
    urlFinalizer?: (layoutPath: string) => Promise<string>;
}

/**
 * This class provides 'Open In' buttons.  The primary type is presented in the 'default' button, while other
 * allowed types are presented by the 'dropdown' button.
 *
 * When the user uses a particular type, that type becomes their default (that type value is saved in local storage for
 * the screen/action in which they are working).
 */
export class OpenButtons {
    protected allowedTypes: OpenType[];
    protected buttonOpenDefault: Button;
    protected buttonOpenDropdown: Button;
    protected defaultType: OpenType;
    private _defaultTypeForUser: OpenType;
    protected doBeforeOpen: (type: OpenType) => Promise<void>;
    protected doAfterOpen: (type: OpenType) => Promise<void>;
    protected itemDescr: string;
    protected layoutPath: string;
    protected localSettingsKey: string;
    protected urlFinalizer?: (layoutPath: string) => Promise<string>;

    constructor(props: OpenButtonsProps) {
        this.validateProps(props);
        this.loadFromProps(props);
        this.createDefaultButton(props.parentComponent);
        this.createDropdownButton(props.parentComponent);
    }

    private validateProps(props: OpenButtonsProps) {
        if (ArrayUtil.isEmptyArray(props.allowedTypes)) {
            throw new Error("At least one open type must be provided");
        }
        if (props.defaultType == null) {
            throw new Error("The default open type must be provided");
        }
        if (StringUtil.isEmptyString(props.itemDescr)) {
            throw new Error("The description of the item being opened must be provided");
        }
        if (StringUtil.isEmptyString(props.layoutPath)) {
            throw new Error("The target layout path must be provided");
        }
        if (StringUtil.isEmptyString(props.localSettingsKey)) {
            throw new Error("The local settings key must be provided");
        }
    }

    private loadFromProps(props: OpenButtonsProps) {
        this.allowedTypes = props.allowedTypes;
        this.defaultType = props.defaultType;
        this.doBeforeOpen = props.doBeforeOpen;
        this.doAfterOpen = props.doAfterOpen;
        this.itemDescr = props.itemDescr;
        this.layoutPath = props.layoutPath;
        this.localSettingsKey = props.localSettingsKey;
        this.urlFinalizer = props.urlFinalizer;
    }

    private createDefaultButton(parentComponent: Component) {
        const multipleTypesAllowed = this.multipleTypesAllowed;
        this.buttonOpenDefault = new Button({
            id: parentComponent.createInnerComponentId("buttonOpenDefault"),
            borderWidth: 0,
            borderTopRightRadius: multipleTypesAllowed ? 0: null,
            borderBottomRightRadius: multipleTypesAllowed ? 0: null,
            marginRight: multipleTypesAllowed ? 0: null,
            paddingLeft: 4,
            paddingRight: multipleTypesAllowed ? 0: null,
            onClick: () => this.open(this.defaultTypeForUser),
            ...this.getPropsForType(this.defaultTypeForUser)
        });
    }

    private createDropdownButton(parentComponent: Component) {
        if (this.multipleTypesAllowed) {
            this.buttonOpenDropdown = new Button({
                id: parentComponent.createInnerComponentId("buttonOpenDropdown"),
                borderWidth: 0,
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0,
                imageName: ImageNames.arrow,
                imageWidth: 12,
                imageHeight: 12,
                margin: 0,
                padding: 8,
                paddingLeft: 0,
                paddingRight: 2,
                contextMenuItems: () => this.getDetailDropdownItems(),
                tooltip: `Show other options for opening the ${this.itemDescr}`
            });
        }
    }

    get(): Button[] {
        const result = [ this.buttonOpenDefault ];
        if (this.buttonOpenDropdown != null) {
            result.push(this.buttonOpenDropdown);
        }
        return result;
    }

    get defaultButton(): Button {
        return this.buttonOpenDefault;
    }

    get dropdownButton(): Button {
        return this.buttonOpenDropdown;
    }

    enableAll(value: boolean) {
        this.buttonOpenDefault.enabled = value;
        if (this.buttonOpenDropdown != null) {
            this.buttonOpenDropdown.enabled = value;
        }
    }

    private typeAllowed(type: OpenType): boolean {
        return this.allowedTypes.includes(type);
    }

    private multipleTypesAllowed(): boolean {
        return this.allowedTypes.length > 1;
    }

    public get defaultTypeForUser(): OpenType {
        if (this._defaultTypeForUser == null) {
            this._defaultTypeForUser = this.getDefaultTypeFromLocalStorage() ?? this.defaultType;
        }
        return this._defaultTypeForUser;
    }

    set defaultTypeForUser(value: OpenType) {
        if (this._defaultTypeForUser === value)
            return;
        this._defaultTypeForUser = value;
        this.syncDefaultType();
    }

    private syncDefaultType() {
        // Avoid saving default type to local storage if it's the same as the default value of NEW_WINDW
        const valueToStore = this.defaultType === this.defaultTypeForUser ? null : this._defaultTypeForUser;
        LocalUserSettings.set(this.localSettingsKey, valueToStore);
        this.buttonOpenDefault.setProps(this.getPropsForType(this.defaultTypeForUser));
    }

    private getDefaultTypeFromLocalStorage(): OpenType | null {
        const storedValue = LocalUserSettings.getString(this.localSettingsKey);
        if (StringUtil.isEmptyString(storedValue)) {
            return null;
        }
        if (!ObjectUtil.isEnumValue(OpenType, storedValue) || !this.typeAllowed(storedValue)) {
            LocalUserSettings.set(this.localSettingsKey, null);
            return null;
        }
        return storedValue;
    }

    protected getPropsForType(type: OpenType): Partial<ButtonProps> {
        switch (type) {
            case OpenType.SAME_TAB:
                return {
                    imageName: ImageNames.detail,
                    tooltip: `Open the ${this.itemDescr} in this browser tab`
                };
            case OpenType.NEW_TAB_NO_SWITCH:
            case OpenType.NEW_TAB_WITH_SWITCH:
                return {
                    imageName: ImageNames.tab,
                    tooltip: `Open the ${this.itemDescr} in a new browser tab`
                };
            case OpenType.NEW_WINDOW:
                return {
                    imageName: ImageNames.overlappingWindows,
                    tooltip: `Open the ${this.itemDescr} in a new browser window`
                };
            default:
                throw new Error("Unexpected open type provided");
        }
    }

    private getDetailDropdownItems(): Partial<ContextMenuLabelProps>[] {
        const result: Partial<ContextMenuLabelProps>[] = [];
        this.addDetailDropdownItem(result, OpenType.SAME_TAB, "Open");
        this.addDetailDropdownItem(result, OpenType.NEW_TAB_WITH_SWITCH, "Open in New Tab");
        this.addDetailDropdownItem(result, OpenType.NEW_WINDOW, "Open in New Window");
        return result;
    }

    private addDetailDropdownItem(array: Partial<ContextMenuLabelProps>[], type: OpenType, text: string) {
        if (this.allowedTypes.includes(type) && type !== this.defaultTypeForUser) {
            array.push({
                text,
                onClick: () => {
                    this.defaultTypeForUser = type;
                    this.open(type);
                }
            });
        }
    }

    protected async open(type: OpenType) {
        try {
            this.defaultButton.busy = true;
            let url = this.layoutPath;
            if (this.urlFinalizer != null) {
                url = await this.urlFinalizer?.(this.layoutPath);
            }
            if (this.doBeforeOpen != null) {
                await this.doBeforeOpen(type);
            }

            if (type === OpenType.SAME_TAB) {
                await this.openInSameTab(url);
            } else if (type === OpenType.NEW_TAB_WITH_SWITCH) {
                await this.openInNewTab(url);
            } else if (type === OpenType.NEW_TAB_NO_SWITCH) {
                await this.openInNewTab(url);
                window.focus();
            } else if (type === OpenType.NEW_WINDOW) {
                await this.openInNewWindow(url);
            }

            if (this.doAfterOpen != null) {
                await this.doAfterOpen(type);
            }
        } finally {
            this.defaultButton.busy = false;
        }
    }

    /**
     * Opens the url in the current tab.
     * @param url The layout path which may include query parameters
     */
    protected async openInSameTab(url: string) {
        await Navigation.navigateTo(url);
    }

    /**
     * Opens the url in a new tab (in the same window).
     * @param url The layout path which may include query parameters
     */
    protected async openInNewTab(url: string) {
        await Navigation.navigateTo(url, { newTab: true });
    }

    /**
     * Opens the url in a new window that's the same size as the current window, and centers it on the screen.
     * @param url The layout path which may include query parameters
     */
    protected async openInNewWindow(url: string) {
        const windowSize = {
            width: window.innerWidth,
            height: window.innerHeight,
        };
        const loc = {
            left: ((window.screen as any).availLeft + (window.screen.availWidth / 2)) - (windowSize.width / 2),
            top: ((window.screen as any).availTop + (window.screen.availHeight / 2)) - (windowSize.height / 2)
        };
        const popoutNavOptions = {
            left: loc.left,
            top: loc.top,
            height: windowSize.height,
            width: windowSize.width,
            newTab: true
        };
        await Navigation.navigateTo(url, popoutNavOptions);
    }
}
